import { Switch as MuiSwitch, SwitchProps as MuiSwitchProps, styled } from '@mui/material';
import { forwardRef } from 'react';

import { colorSystem } from '../../theme';

export type SwitchProps = Omit<MuiSwitchProps, 'size'> & {
    size?: 'large' | 'small';
};

export const Switch = forwardRef(
    ({ size = 'small', ...props }: SwitchProps, ref: React.Ref<HTMLButtonElement>): JSX.Element => {
        switch (size) {
            case 'small':
                return <SmallSwitch ref={ref} {...props} />;
            case 'large':
                return <LargeSwitch ref={ref} {...props} />;
        }
    },
);

const DefaultSwitch = styled(MuiSwitch)({
    padding: 0,
    // MUI clips the root to hide its ripple; without padding, that would also cut off the focus ring on the track.
    overflow: 'visible',
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&:hover': {
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.neutral[7],
            },
        },
        '&.Mui-checked': {
            color: colorSystem.neutral.white,
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.primary[5],
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: colorSystem.primary[4],
            },
        },
        '&.Mui-checked:hover': {
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.primary[7],
            },
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: colorSystem.neutral.white,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: colorSystem.neutral[3],
        },
    },
    '& .MuiSwitch-track': {
        backgroundColor: colorSystem.neutral[5],
        opacity: 1,
    },
});

const SmallSwitch = styled(DefaultSwitch)({
    width: 28,
    height: 16,
    '& .MuiSwitch-switchBase': {
        '&.Mui-checked': {
            transform: 'translateX(12px)',
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 12,
        height: 12,
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
    },
});

const LargeSwitch = styled(DefaultSwitch)({
    width: 40,
    height: 24,
    '& .MuiSwitch-switchBase': {
        '&.Mui-checked': {
            transform: 'translateX(16px)',
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
    },
    '& .MuiSwitch-track': {
        borderRadius: 24 / 2,
    },
});
